import React, {useEffect, useMemo, useState} from 'react';
import {
    Alert,
    Box,
    Button,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import {useLocation, useNavigate, useParams, useSearchParams} from 'react-router';
import {useSession} from '../../../firebase/UserProvider';
import {getUserDoc} from '../../../firebase/firestore/users';
import AuthorChip from '../../products/components/common/AuthorChip';
import ClassificationFieldsGroup from '../../../components/classification/ClassificationFieldsGroup';
import {
    createCollectionTask,
    getCollectionTask,
    updateCollectionTask,
} from '../collectionTaskApi';
import {getEei} from '../../../firebase/firestore/eei';
import {
    CT_PRIORITY_OPTIONS,
    CT_STATUSES,
    CM_ROLES,
    hasAnyRole,
    highestLinkedEeiPriority,
    levelToCtClassification,
    normalizeCtClassificationToLevel,
    statusLabel,
} from '../collectionTaskPayload';
import IcrEeiLinker from '../components/IcrEeiLinker';
import CollectorAssigneeSelect from '../components/CollectorAssigneeSelect';
import AutofillButton from '../components/AutofillButton';
import RelatedRequirementsFromSelection from '../components/RelatedRequirementsFromSelection';
import EntityTargetsInput from '../../collection-requests/components/EntityTargetsInput';

const RESERVED_TASK_IDS = new Set(['new', 'create']);

function isEditTaskRoute(taskId) {
    return Boolean(taskId) && !RESERVED_TASK_IDS.has(String(taskId).toLowerCase());
}

function sessionAsAvatarProfile(user) {
    if (!user) return null;
    const displayName = user.displayName || user.name || user.email || null;
    return {
        displayName,
        hasAvatar: user.hasAvatar === true,
        profilePicture: user.profilePicture || user.photoURL || undefined,
    };
}

function emptyForm() {
    return {
        title: '',
        description: '',
        status: 'planned',
        priority: 'Medium',
        classification: 'UNCLASSIFIED',
        classificationLevel: 'U',
        distribution: [],
        compartment: [],
        assignee: null,
        eeis: [],
        targets: [],
    };
}

export default function CollectionTaskFormPage() {
    const {taskId} = useParams();
    const isEdit = isEditTaskRoute(taskId);
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const {user, role, roles} = useSession();
    const canManage = hasAnyRole(role, roles, CM_ROLES);

    const [form, setForm] = useState(emptyForm);
    const [createdBy, setCreatedBy] = useState(user?.uid || user?.id || '');
    const [createdAt, setCreatedAt] = useState(null);
    const [creatorProfile, setCreatorProfile] = useState(() => sessionAsAvatarProfile(user));
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [autofillError, setAutofillError] = useState(null);
    const [loaded, setLoaded] = useState(!isEdit);

    const creatorId = createdBy || user?.uid || user?.id || '';
    const creatorDisplayName =
        creatorProfile?.displayName
        || (creatorId && (creatorId === user?.uid || creatorId === user?.id)
            ? (user?.displayName || user?.name || user?.email)
            : null)
        || creatorId
        || 'Unknown';

    const eeiIds = useMemo(() => form.eeis.map((e) => e.id).filter(Boolean), [form.eeis]);

    useEffect(() => {
        if (!creatorId) {
            setCreatorProfile(null);
            return undefined;
        }
        if (creatorId === user?.uid || creatorId === user?.id) {
            setCreatorProfile(sessionAsAvatarProfile(user));
            return undefined;
        }
        let cancelled = false;
        getUserDoc(creatorId)
            .then((doc) => {
                if (cancelled || !doc) return;
                setCreatorProfile({
                    displayName: doc.displayName || doc.name || doc.email || null,
                    hasAvatar: doc.hasAvatar === true,
                    profilePicture: doc.profilePicture || doc.photoURL || undefined,
                });
            })
            .catch(() => {
                if (!cancelled) setCreatorProfile(null);
            });
        return () => { cancelled = true; };
    }, [creatorId, user]);

    useEffect(() => {
        if (!isEdit) return undefined;
        let cancelled = false;
        getCollectionTask(taskId)
            .then((task) => {
                if (cancelled || !task) return;
                setForm({
                    title: task.title || '',
                    description: task.description || '',
                    status: task.status || 'planned',
                    priority: task.priority || 'Medium',
                    classification: task.classification || 'UNCLASSIFIED',
                    classificationLevel: task.classificationLevel
                        || normalizeCtClassificationToLevel(task.classification),
                    distribution: Array.isArray(task.distribution) ? task.distribution : [],
                    compartment: Array.isArray(task.compartment) ? task.compartment : [],
                    assignee: task.assigneeId ? {id: task.assigneeId} : null,
                    eeis: Array.isArray(task.eeis) ? task.eeis : [],
                    targets: Array.isArray(task.targets) ? task.targets : [],
                });
                setCreatedBy(task.createdBy || '');
                setCreatedAt(task.createdAt || null);
                setLoaded(true);
            })
            .catch((e) => {
                if (!cancelled) {
                    setError(e?.message || 'Failed to load task');
                    setLoaded(true);
                }
            });
        return () => { cancelled = true; };
    }, [isEdit, taskId]);

    // Prefill from EEI page / query params
    useEffect(() => {
        if (isEdit) return;
        const state = location.state || {};
        const eeis = Array.isArray(state.eeis) ? state.eeis : [];
        const prefill = state.prefill || null;
        const runAutofill = state.runAutofill || searchParams.get('autofill') === '1';

        if (eeis.length) {
            const nextEeis = eeis.map((e) => ({
                id: e.id,
                eeiNumber: e.eeiNumber || e.eei_number,
                text: e.text,
                priority: e.priority,
                icrId: e.icrId || e.icr_id || null,
            })).filter((e) => e.id);
            setForm((prev) => ({
                ...prev,
                eeis: nextEeis,
                title: prefill?.suggestedTitle || prev.title,
                description: prefill?.description || prev.description,
                classification: prefill?.classification || prev.classification,
                classificationLevel: prefill?.classification
                    ? normalizeCtClassificationToLevel(prefill.classification)
                    : prev.classificationLevel,
                priority: prefill?.priority || highestLinkedEeiPriority(nextEeis),
                // Pin so linked-EEI auto-derivation does not silently override
                // the suggested priority (still user-editable).
                _priorityPinned: Boolean(prefill?.priority),
            }));
            if (runAutofill && nextEeis.length) {
                setForm((prev) => ({...prev, _pendingAutofill: true}));
            }
        } else if (searchParams.get('eeiId')) {
            const eeiId = searchParams.get('eeiId');
            setForm((prev) => ({
                ...prev,
                eeis: [{id: eeiId, text: eeiId}],
                _pendingAutofill: searchParams.get('autofill') === '1',
            }));
        }
    }, [isEdit, location.state, searchParams]);

    useEffect(() => {
        if (isEdit) return undefined;
        const ids = form.eeis.filter((e) => e.id && !e.priority).map((e) => e.id);
        if (!ids.length) return undefined;
        let cancelled = false;
        Promise.all(ids.map((id) => getEei(id)))
            .then((results) => {
                if (cancelled) return;
                const byId = new Map();
                for (const eei of results) {
                    if (eei?.id) byId.set(eei.id, eei);
                }
                setForm((prev) => {
                    const eeis = prev.eeis.map((e) => {
                        if (e.priority || !byId.has(e.id)) return e;
                        const loaded = byId.get(e.id);
                        return {
                            ...e,
                            priority: loaded.priority || 'Medium',
                            eeiNumber: e.eeiNumber || loaded.eeiNumber,
                            text: e.text && e.text !== e.id ? e.text : (loaded.text || e.text),
                        };
                    });
                    return {
                        ...prev,
                        eeis,
                        priority: prev._priorityPinned
                            ? prev.priority
                            : highestLinkedEeiPriority(eeis),
                    };
                });
            })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [form.eeis, isEdit]);

    useEffect(() => {
        if (isEdit) return;
        setForm((prev) => {
            if (prev._priorityPinned) return prev;
            const next = highestLinkedEeiPriority(prev.eeis);
            return prev.priority === next ? prev : {...prev, priority: next};
        });
    }, [form.eeis, isEdit]);

    useEffect(() => {
        if (!form._pendingAutofill) return;
        const idsEei = form.eeis.map((e) => e.id).filter(Boolean);
        if (!idsEei.length) return;
        let cancelled = false;
        import('../collectionTaskApi').then(({autofillCollectionTask}) => {
            autofillCollectionTask({eeiIds: idsEei})
                .then((result) => {
                    if (cancelled || !result) return;
                    setForm((prev) => ({
                        ...prev,
                        title: result.title || prev.title,
                        description: result.description || prev.description,
                        classification: result.classification || prev.classification,
                        classificationLevel: result.classification
                            ? normalizeCtClassificationToLevel(result.classification)
                            : prev.classificationLevel,
                        _pendingAutofill: false,
                    }));
                })
                .catch((err) => {
                    if (!cancelled) {
                        setAutofillError(err?.message || 'Autofill failed');
                        setForm((prev) => ({...prev, _pendingAutofill: false}));
                    }
                });
        });
        return () => { cancelled = true; };
    }, [form._pendingAutofill, form.eeis]);

    const setField = (key, value) => setForm((prev) => ({...prev, [key]: value}));

    const handleSave = async () => {
        if (!form.title.trim()) {
            setError('Title is required');
            return;
        }
        setSaving(true);
        setError(null);
        try {
            const payload = {
                title: form.title.trim(),
                description: form.description,
                priority: form.priority,
                classification: form.classification,
                classificationLevel: form.classificationLevel,
                distribution: form.distribution,
                compartment: form.compartment,
                assigneeId: form.assignee?.id || null,
                eeiIds,
                targets: Array.isArray(form.targets) ? form.targets : [],
            };
            const navState = {returnTo: '/collection/tasks'};
            if (isEdit) {
                payload.status = form.status;
                const updated = await updateCollectionTask(taskId, payload);
                navigate(`/collection/tasks/${encodeURIComponent(
                    updated?.ctNumber || taskId,
                )}`, {state: navState});
            } else {
                const created = await createCollectionTask(payload);
                navigate(`/collection/tasks/${encodeURIComponent(
                    created.ctNumber || created.id,
                )}`, {state: {...navState, refetchTargets: true}});
            }
        } catch (e) {
            setError(e?.message || 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    if (!loaded) {
        return <Box p={2}><Typography>Loading…</Typography></Box>;
    }

    return (
        <Box p={2} maxWidth={900} mx="auto">
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h5">{isEdit ? 'Edit Collection Task' : 'New Collection Task'}</Typography>
                <Button onClick={() => navigate('/collection/tasks')}>Back</Button>
            </Stack>

            {error && <Alert severity="error" sx={{mb: 2}}>{error}</Alert>}
            {autofillError && <Alert severity="warning" sx={{mb: 2}}>{autofillError}</Alert>}

            <Stack spacing={2}>
                <Stack direction={{xs: 'column', sm: 'row'}} spacing={2} alignItems="flex-start">
                    <Box sx={{flex: 1, minWidth: 0}}>
                        <IcrEeiLinker
                            eeis={form.eeis}
                            disabled={!canManage && isEdit}
                            onChange={({eeis}) => setForm((prev) => ({
                                ...prev,
                                eeis,
                                priority: (isEdit || prev._priorityPinned)
                                    ? prev.priority
                                    : highestLinkedEeiPriority(eeis),
                            }))}
                        />
                    </Box>

                    <Box sx={{width: {xs: '100%', sm: 240}, flexShrink: 0}}>
                        <Typography variant="subtitle2" gutterBottom>Priority</Typography>
                        <FormControl fullWidth>
                            <Select
                                aria-label="Priority"
                                value={form.priority}
                                onChange={(e) => setField('priority', e.target.value)}
                            >
                                {CT_PRIORITY_OPTIONS.map((p) => (
                                    <MenuItem key={p} value={p}>{p}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                </Stack>

                {canManage && (
                    <RelatedRequirementsFromSelection
                        eeiIds={eeiIds}
                        linkedEeis={form.eeis}
                        onAdd={({type, item}) => {
                            if (type !== 'EEI') return;
                            setForm((prev) => {
                                if (prev.eeis.some((e) => e.id === item.id)) return prev;
                                const eeis = [...prev.eeis, item];
                                return {
                                    ...prev,
                                    eeis,
                                    priority: (isEdit || prev._priorityPinned)
                                        ? prev.priority
                                        : highestLinkedEeiPriority(eeis),
                                };
                            });
                        }}
                    />
                )}

                <AutofillButton
                    eeiIds={eeiIds}
                    onResult={(result, err) => {
                        if (err) {
                            setAutofillError(err.message || 'Autofill failed');
                            return;
                        }
                        setAutofillError(null);
                        if (result) {
                            setForm((prev) => ({
                                ...prev,
                                title: result.title || prev.title,
                                description: result.description || prev.description,
                                classification: result.classification || prev.classification,
                                classificationLevel: result.classification
                                    ? normalizeCtClassificationToLevel(result.classification)
                                    : prev.classificationLevel,
                            }));
                        }
                    }}
                />

                <TextField
                    label="Title"
                    required
                    fullWidth
                    value={form.title}
                    onChange={(e) => setField('title', e.target.value)}
                />
                <TextField
                    label="Description"
                    fullWidth
                    multiline
                    minRows={4}
                    value={form.description}
                    onChange={(e) => setField('description', e.target.value)}
                />

                <EntityTargetsInput
                    value={form.targets}
                    onChange={(targets) => setField('targets', targets)}
                />

                {isEdit && canManage && (
                    <FormControl sx={{maxWidth: {sm: 300}}} fullWidth>
                        <InputLabel>Status</InputLabel>
                        <Select
                            label="Status"
                            value={form.status}
                            onChange={(e) => setField('status', e.target.value)}
                        >
                            {CT_STATUSES.map((s) => (
                                <MenuItem key={s} value={s}>{statusLabel(s)}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}

                <ClassificationFieldsGroup
                    idPrefix="ct-classification"
                    level={form.classificationLevel}
                    onLevelChange={(level) => setForm((prev) => ({
                        ...prev,
                        classificationLevel: level,
                        // Single source of truth for classification: keep the legacy free-string
                        // field (still sent to the API / shown as a Chip on the Detail page) in
                        // sync so it never drifts from the canonical Level select shown here.
                        classification: levelToCtClassification(level),
                    }))}
                    distribution={form.distribution}
                    onDistributionChange={(distribution) => setField('distribution', distribution)}
                    compartment={form.compartment}
                    onCompartmentChange={(compartment) => setField('compartment', compartment)}
                />

                {canManage && (
                    <CollectorAssigneeSelect
                        value={form.assignee}
                        onChange={(v) => setField('assignee', v)}
                    />
                )}

                {isEdit && (
                    <Stack direction="row" spacing={2} alignItems="center">
                        <Box sx={{flex: 1, minWidth: 0}}>
                            <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                                Created by
                            </Typography>
                            {creatorId ? (
                                <AuthorChip
                                    userId={creatorId}
                                    userDisplayName={creatorDisplayName}
                                    flex={undefined}
                                    avatarProfile={creatorProfile}
                                />
                            ) : (
                                <Typography variant="body2" color="text.secondary">—</Typography>
                            )}
                        </Box>
                        <TextField
                            label="Created at"
                            value={createdAt ? new Date(createdAt).toLocaleString() : '—'}
                            disabled
                            fullWidth
                            sx={{flex: 1}}
                        />
                    </Stack>
                )}

                <Stack direction="row" spacing={2} justifyContent="flex-end">
                    <Button onClick={() => navigate('/collection/tasks')}>Cancel</Button>
                    <Button variant="contained" onClick={handleSave} disabled={saving}>
                        {saving ? 'Saving…' : 'Save'}
                    </Button>
                </Stack>
            </Stack>
        </Box>
    );
}
